import { t, tf } from "./i18n.js";
// ---------------------------------------------------------------------------
// Erweiterte 501-Statistiken ("Advanced X01") auf der Statistikseite
//
// Vorbild sind die Advanced-X01-Ansichten etablierter Plattformen:
// Aktivitaet, Leistungs-KPIs, Verlaeufe, Verteilungen und Top-10-Listen.
// ALLE Zahlen hier kommen aus Daten, die die App HEUTE schon schreibt:
//
//   * match_history_501 - je Match: match_average, darts_thrown,
//     highest_finish, is_win, created_at und match_details mit ALLEN Legs
//     samt Wurfliste je Aufnahme ({old, thrown, new}, game-501.js
//     recordLegStat501). Das ist die Quelle fuer alles Zeitliche und
//     alle Verteilungen.
//   * stats_501 - kumulierte Konto-Werte (Checkout-Quote etc.), die der
//     501-Reiter ohnehin laedt; sie werden hier nur durchgereicht.
//
// BEWUSST NICHT GEBAUT, weil die Daten (noch) nicht erfasst werden -
// lieber eine ehrliche Luecke als eine geschaetzte Zahl:
//   * Spielzeit/Laufstrecke: es gibt keine Zeitstempel je Aufnahme.
//   * Doppel-Trefferbild je Feld (D1-D20-Radar): erfasst wird das
//     getroffene Feld (dart_throws, nur Kamera), nicht das ANVISIERTE.
//   * Checkout-%-Verlauf: checkoutAttempts/Hits stehen im kumulierten
//     stats_501, nicht je Match.
// Was dafuer zusaetzlich zu erfassen waere, steht in der Umsetzungsdoku.
//
// Rendering: Chart.js (liegt als window.Chart bereit), dieselbe Optik wie
// renderProfileChart in profile.js. Alle Berechnungen laufen clientseitig
// auf maximal ERW_LIMIT juengsten Matches - eine Abfrage je Zeitraum,
// Filter sind reine Array-Filter.
// ---------------------------------------------------------------------------

const ERW_LIMIT = 400; // juengste Matches je Abfrage

const erwZustand = {
  zeitraumTage: 30, // 0 = alles
  rohdaten: null,
  geladenFuerTage: null,
  charts: [], // lebende Chart.js-Instanzen (fuer destroy beim Neuaufbau)
};

// ── Laden ──────────────────────────────────────────────────────────────────

async function ladeErw501Matches() {
  if (
    erwZustand.rohdaten !== null &&
    erwZustand.geladenFuerTage === erwZustand.zeitraumTage
  ) {
    return erwZustand.rohdaten;
  }
  try {
    let query = window._supabase
      .from("match_history_501")
      .select(
        "created_at,player_name,opponent_name,is_win,match_average," +
          "darts_thrown,highest_finish,match_details",
      )
      .eq("user_id", window.currentUser.id)
      .order("created_at", { ascending: false })
      .limit(ERW_LIMIT);
    if (erwZustand.zeitraumTage > 0) {
      const ab = new Date(
        Date.now() - erwZustand.zeitraumTage * 24 * 3600 * 1000,
      );
      query = query.gte("created_at", ab.toISOString());
    }
    const { data, error } = await query;
    if (error) throw error;
    erwZustand.rohdaten = data || [];
  } catch (err) {
    console.warn("Erweiterte 501-Stats:", err?.message || err);
    erwZustand.rohdaten = [];
  }
  erwZustand.geladenFuerTage = erwZustand.zeitraumTage;
  return erwZustand.rohdaten;
}

// ── Auswertung ─────────────────────────────────────────────────────────────

// "thrown" kann eine Zahl sein ODER Text wie "Bust (45)" (Anzeigeformat
// seit 13.08.). Ein Bust zaehlt als 0 geworfene Punkte - genau so rechnet
// auch die Spiellogik den Rest zurueck.
function wurfPunkte(eintrag) {
  const roh = eintrag?.thrown;
  if (typeof roh === "number" && Number.isFinite(roh)) return roh;
  const text = String(roh ?? "");
  if (/bust/i.test(text)) return 0;
  const zahl = parseInt(text, 10);
  return Number.isFinite(zahl) ? zahl : null;
}

// Zaehlt ein GEWONNENES Leg fuer Bestwerte (Bestes Leg, Top-10,
// Darts-je-Leg, Finishes)? Befund 14.08.2026: In der Historie lag ein
// "gewonnenes" Leg mit 3 Darts - Rumpfdaten eines abgebrochenen Legs.
// Zwei physische Tore schneiden solche Eintraege ab, ohne echte kurze
// Varianten (Start 170 -> 3 Darts) zu verlieren:
//   1. Rest muss 0 sein (gewonnen ohne Auswerfen gibt es nicht),
//   2. Dartzahl >= physisches Minimum fuer die Startpunktzahl
//      (je Dart max 60, letzter max Bullseye 50; 501 -> 9 Darts).
// In die AKTIVITAET (Darts, Aufnahmen, Scoring-Baender) zaehlen die
// Wuerfe solcher Legs weiter - geworfen wurden sie ja.
function legZaehltFuerBestwerte(leg) {
  if (!leg.gewonnen || leg.rest !== 0 || leg.darts <= 0) return false;
  const start = Number(leg.start) || 501;
  const minimum =
    typeof window.mindestDarts501 === "function"
      ? window.mindestDarts501(start)
      : start <= 50
        ? 1
        : Math.ceil((start - 50) / 60) + 1;
  return leg.darts >= minimum;
}

function meineLegs(match) {
  const legs = Array.isArray(match.match_details) ? match.match_details : [];
  const ich = match.player_name;
  return legs.map((leg) => {
    const binP1 = leg.p1_name === ich;
    const history = (binP1 ? leg.p1_history : leg.p2_history) || [];
    return {
      history,
      darts: Number(binP1 ? leg.p1_darts : leg.p2_darts) || 0,
      rest: Number(binP1 ? leg.p1_rest : leg.p2_rest) || 0,
      gewonnen: leg.winner === ich,
      checkout: Number(leg.checkout) || 0,
      // Startpunktzahl aus der ersten Aufnahme ablesen (301/501/701) -
      // sie steht nicht am Leg, aber jede Historie beginnt bei ihr.
      start: Number(history[0]?.old) || 501,
    };
  });
}

function werteAus(matches) {
  const a = {
    matches: matches.length,
    siege: 0,
    legs: 0,
    darts: 0,
    aufnahmen: 0,
    baender: { "<60": 0, "60+": 0, "100+": 0, "140+": 0, "170+": 0, 180: 0 },
    count180: 0,
    dartsProTag: new Map(), // "MM-TT" -> Summe
    avgVerlauf: [], // je Match: {datum, avg, first9}
    dartsJeLeg: [], // nur gewonnene Legs
    finishes: [], // Checkout-Hoehe je gewonnenem Leg
    topLegs: [], // {darts, avg, datum, gegner}
    topFinishes: [], // {score, datum, gegner}
    besterAvg: 0,
    bestesLeg: 0,
    besterCheckout: 0,
  };

  for (const match of [...matches].reverse()) {
    const datum = new Date(match.created_at);
    const tag =
      String(datum.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(datum.getDate()).padStart(2, "0");
    const datumText = datum.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    if (match.is_win) a.siege += 1;
    a.besterAvg = Math.max(a.besterAvg, Number(match.match_average) || 0);

    let matchDarts = 0;
    let first9Score = 0;
    let first9Darts = 0;

    for (const leg of meineLegs(match)) {
      a.legs += 1;
      matchDarts += leg.darts;

      leg.history.forEach((eintrag, i) => {
        const punkte = wurfPunkte(eintrag);
        if (punkte === null) return;
        a.aufnahmen += 1;
        if (punkte === 180) {
          a.baender["180"] += 1;
          a.count180 += 1;
        } else if (punkte >= 170) a.baender["170+"] += 1;
        else if (punkte >= 140) a.baender["140+"] += 1;
        else if (punkte >= 100) a.baender["100+"] += 1;
        else if (punkte >= 60) a.baender["60+"] += 1;
        else a.baender["<60"] += 1;
        if (i < 3) {
          first9Score += punkte;
          first9Darts += 3;
        }
      });

      if (legZaehltFuerBestwerte(leg)) {
        a.dartsJeLeg.push(leg.darts);
        a.bestesLeg = a.bestesLeg
          ? Math.min(a.bestesLeg, leg.darts)
          : leg.darts;
        a.topLegs.push({
          darts: leg.darts,
          avg: ((leg.start / leg.darts) * 3).toFixed(2),
          datum: datumText,
          gegner: match.opponent_name || "-",
        });
        if (leg.checkout > 0) {
          a.finishes.push(leg.checkout);
          a.besterCheckout = Math.max(a.besterCheckout, leg.checkout);
          a.topFinishes.push({
            score: leg.checkout,
            datum: datumText,
            gegner: match.opponent_name || "-",
          });
        }
      }
    }

    a.darts += matchDarts || Number(match.darts_thrown) || 0;
    a.dartsProTag.set(
      tag,
      (a.dartsProTag.get(tag) || 0) + (matchDarts || Number(match.darts_thrown) || 0),
    );
    a.avgVerlauf.push({
      datum: tag,
      avg: Number(match.match_average) || 0,
      first9: first9Darts ? +((first9Score / first9Darts) * 3).toFixed(2) : null,
    });
  }

  a.topLegs.sort((x, y) => x.darts - y.darts || y.avg - x.avg);
  a.topLegs = a.topLegs.slice(0, 10);
  a.topFinishes.sort((x, y) => y.score - x.score);
  a.topFinishes = a.topFinishes.slice(0, 10);
  return a;
}

// ── Zeichnen ───────────────────────────────────────────────────────────────

const ERW_ACHSE = {
  x: { ticks: { color: "#94a3b8" }, grid: { display: false } },
  y: {
    beginAtZero: true,
    grace: "10%",
    ticks: { color: "#94a3b8" },
    grid: { color: "rgba(255,255,255,0.06)" },
  },
};

function erwChart(canvas, config) {
  if (typeof Chart === "undefined") return;
  erwZustand.charts.push(new Chart(canvas, config));
}

function erwKpi(label, wert, accent) {
  return `<div class="profile-kpi"><div class="profile-kpi-value"${
    accent ? ` style="color:${accent}"` : ""
  }>${wert}</div><div class="profile-kpi-label">${label}</div></div>`;
}

function erwKarte(id, titel) {
  return `<div class="profile-chart-wrap erw-karte">
    <div class="profile-chart-title">${titel}</div>
    <canvas id="${id}" height="200"></canvas></div>`;
}

function erwTabelle(titel, spalten, zeilen) {
  const kopf = spalten.map((s) => `<th>${s}</th>`).join("");
  const rumpf = zeilen.length
    ? zeilen
        .map(
          (zeile, i) =>
            `<tr><td>${i + 1}</td>${zeile
              .map((z) => `<td>${z}</td>`)
              .join("")}</tr>`,
        )
        .join("")
    : `<tr><td colspan="${spalten.length + 1}" style="color:var(--text-muted)">${t("js.stats501.002")}</td></tr>`;
  return `<div class="glass-panel erw-tabelle"><b>${titel}</b>
    <table><thead><tr><th>#</th>${kopf}</tr></thead>
    <tbody>${rumpf}</tbody></table></div>`;
}

// Histogramm-Hilfe: Werte -> fortlaufende Klassen (Klassenbreite 1)
function histogramm(werte) {
  if (!werte.length) return { labels: [], counts: [] };
  const min = Math.min(...werte);
  const max = Math.max(...werte);
  const labels = [];
  const counts = [];
  for (let v = min; v <= max; v++) {
    labels.push(String(v));
    counts.push(0);
  }
  for (const w of werte) counts[w - min] += 1;
  return { labels, counts };
}

async function renderErweiterte501Stats(container, stats501) {
  // Nur eigenes Profil - dieselbe Linie wie die Heatmap (Datenschutz,
  // und die RLS von match_history_501 filtert ohnehin auf user_id).
  if (window.profileViewIstFremd?.()) return;

  // Alte Chart-Instanzen freigeben, sonst leben ihre Timer weiter.
  for (const c of erwZustand.charts) {
    try {
      c.destroy();
    } catch (e) {
      /* schon weg */
    }
  }
  erwZustand.charts = [];

  container.innerHTML = `<div class="profile-empty">${t("js.stats501.003")}</div>`;
  const matches = await ladeErw501Matches();
  const a = werteAus(matches);

  const opt = (wert, text) =>
    `<option value="${wert}"${
      erwZustand.zeitraumTage === wert ? " selected" : ""
    }>${text}</option>`;

  // Checkout-Quote aus dem kumulierten stats_501, falls die Spalten
  // existieren (RPC-Schema) - sonst ehrlich weglassen.
  const coA = Number(stats501?.checkout_attempts ?? stats501?.checkoutAttempts);
  const coH = Number(stats501?.checkout_hits ?? stats501?.checkoutHits);
  const checkoutQuote =
    Number.isFinite(coA) && coA > 0 && Number.isFinite(coH)
      ? Math.round((coH / coA) * 100) + " %"
      : null;

  container.innerHTML = `
    <div class="erw-kopf">
      <div class="profile-chart-title" style="margin:0">Erweiterte Statistiken</div>
      <select id="erw-zeitraum" class="heatmap-select">
        ${opt(7, t("js.heatmap.008"))}${opt(30, t("js.heatmap.009"))}${opt(90, t("js.heatmap.010"))}${opt(0, t("js.heatmap.011"))}
      </select>
    </div>
    ${
      !matches.length
        ? `<div class="profile-empty">${t("js.stats501.004")}</div>`
        : `
    <div class="profile-kpi-grid">
      ${erwKpi(t("js.stats501.005"), a.darts)}
      ${erwKpi(t("js.stats501.015"), a.matches)}
      ${erwKpi("Legs", a.legs)}
      ${erwKpi(t("js.stats501.006"), a.aufnahmen)}
    </div>
    <div class="erw-raster">
      ${erwKarte("erw-darts-tag", t("js.stats501.007"))}
      ${erwKarte("erw-avg", t("js.stats501.008"))}
    </div>
    <div class="profile-kpi-grid">
      ${erwKpi(t("js.stats501.016"), a.besterAvg ? a.besterAvg.toFixed(2) : "–", "var(--accent-green)")}
      ${erwKpi(t("js.friends.012"), a.bestesLeg ? a.bestesLeg + t("js.profile.019") : "–", "var(--accent-blue)")}
      ${erwKpi(t("js.stats501.017"), a.besterCheckout || "–")}
      ${erwKpi("180er", a.count180, "var(--accent-green)")}
      ${erwKpi(t("js.friends.031"), a.matches ? Math.round((a.siege / a.matches) * 100) + " %" : "–")}
      ${checkoutQuote !== null ? erwKpi(t("js.stats501.018"), checkoutQuote) : ""}
    </div>
    <div class="erw-raster">
      ${erwKarte("erw-scoring", t("js.stats501.009"))}
      ${erwKarte("erw-siege", t("js.stats501.010"))}
      ${erwKarte("erw-darts-leg", t("js.stats501.011"))}
      ${erwKarte("erw-finishes", t("js.stats501.001"))}
    </div>
    <div class="erw-raster">
      ${erwTabelle(t("js.stats501.012"), [t("js.stats501.013"), "Avg", t("js.secureonline.013"), t("js.stats501.019")],
        a.topLegs.map((l) => [l.darts, l.avg, escErw(l.gegner), l.datum]))}
      ${erwTabelle(t("js.stats501.020"), ["Score", t("js.secureonline.013"), t("js.stats501.019")],
        a.topFinishes.map((f) => [f.score, escErw(f.gegner), f.datum]))}
    </div>
    ${
      matches.length >= ERW_LIMIT
        ? `<div class="heatmap-hinweis">${tf("js.stats501.014", { p1: ERW_LIMIT })}</div>`
        : ""
    }`
    }`;

  container.querySelector("#erw-zeitraum")?.addEventListener("change", (e) => {
    erwZustand.zeitraumTage = Number(e.target.value);
    renderErweiterte501Stats(container, stats501);
  });

  if (!matches.length) return;

  // ── Darts je Tag ────────────────────────────────────────────────────
  const tage = [...a.dartsProTag.keys()];
  erwChart(container.querySelector("#erw-darts-tag"), {
    type: "bar",
    data: {
      labels: tage,
      datasets: [
        {
          data: tage.map((t) => a.dartsProTag.get(t)),
          backgroundColor: "rgba(59, 130, 246, 0.45)",
          borderColor: "#3b82f6",
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: ERW_ACHSE,
    },
  });

  // ── Average-Verlauf (Match-Avg + First 9) ───────────────────────────
  erwChart(container.querySelector("#erw-avg"), {
    type: "line",
    data: {
      labels: a.avgVerlauf.map((m) => m.datum),
      datasets: [
        {
          label: t("js.stats501.021"),
          data: a.avgVerlauf.map((m) => m.avg),
          borderColor: "#00b06a",
          backgroundColor: "rgba(0, 176, 106, 0.15)",
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 2,
        },
        {
          label: t("js.stats501.022"),
          data: a.avgVerlauf.map((m) => m.first9),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.15)",
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { labels: { color: "#94a3b8" } } },
      scales: ERW_ACHSE,
    },
  });

  // ── Scoring-Verteilung ──────────────────────────────────────────────
  erwChart(container.querySelector("#erw-scoring"), {
    type: "bar",
    data: {
      labels: Object.keys(a.baender),
      datasets: [
        {
          data: Object.values(a.baender),
          backgroundColor: "rgba(59, 130, 246, 0.45)",
          borderColor: "#3b82f6",
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          beginAtZero: true,
          grace: "10%",
          ticks: { color: "#94a3b8" },
          grid: { color: "rgba(255,255,255,0.06)" },
        },
        y: { ticks: { color: "#94a3b8" }, grid: { display: false } },
      },
    },
  });

  // ── Siege/Niederlagen ───────────────────────────────────────────────
  erwChart(container.querySelector("#erw-siege"), {
    type: "doughnut",
    data: {
      labels: [t("js.friends.013"), t("js.stats501.023")],
      datasets: [
        {
          data: [a.siege, a.matches - a.siege],
          backgroundColor: ["rgba(0, 176, 106, 0.6)", "rgba(239, 68, 68, 0.55)"],
          borderColor: ["#00b06a", "#ef4444"],
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: { legend: { labels: { color: "#94a3b8" } } },
    },
  });

  // ── Darts je Leg / Finishes (Histogramme) ───────────────────────────
  for (const [id, werte] of [
    ["#erw-darts-leg", a.dartsJeLeg],
    ["#erw-finishes", a.finishes],
  ]) {
    const h = histogramm(werte);
    erwChart(container.querySelector(id), {
      type: "bar",
      data: {
        labels: h.labels,
        datasets: [
          {
            data: h.counts,
            backgroundColor: "rgba(59, 130, 246, 0.45)",
            borderColor: "#3b82f6",
            borderWidth: 1.5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          ...ERW_ACHSE,
          x: {
            ticks: {
              color: "#94a3b8",
              autoSkip: true,
              maxTicksLimit: 14,
            },
            grid: { display: false },
          },
        },
      },
    });
  }
}

// Gegnernamen kommen aus der Datenbank - vor innerHTML escapen.
function escErw(v) {
  return String(v ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
}

/** Cache leeren (Kontowechsel, neue Matches). */
function resetErweiterte501Stats() {
  erwZustand.rohdaten = null;
  erwZustand.geladenFuerTage = null;
}

// ---------------------------------------------------------------
// Bruecke zu window: profile.js haengt die Sektion an den 501-Reiter.
// ---------------------------------------------------------------
Object.assign(window, { renderErweiterte501Stats, resetErweiterte501Stats });

export { renderErweiterte501Stats, resetErweiterte501Stats };
